// PWA install: capture the (Android/Chrome/Edge) beforeinstallprompt event so
// we can offer an in-app "Install" button on our own terms. iOS has no such
// event — callers fall back to the "Add to Home Screen" hint (see isIOS).
//
// This module MUST be imported early (main.ts) so the listener is registered
// before the browser fires the event.
import { isStandalone } from "./permissions";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((cb) => cb());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    notify();
  });
}

/** True when we hold a deferred prompt and aren't already installed. */
export function canInstall(): boolean {
  return !!deferred && !installed && !isStandalone();
}

/** Fire the native install dialog. Returns true if the user accepted. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  notify();
  return outcome === "accepted";
}

/** Subscribe to install-availability changes; returns an unsubscribe fn. */
export function onInstallChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
